// ============================================================
//  src/notifications.js — Bell de notificaciones in-app
// ============================================================

import { db, auth }    from './firebase.js';
import { collection, query, where, orderBy,
         onSnapshot, limit, getDocs, addDoc,
         updateDoc, doc, serverTimestamp,
         getDoc, writeBatch } from 'firebase/firestore';

let _cancelarEscucha               = null;
let _cancelarEscuchaAdminConcursos = null;
let _cancelarEscuchaAdminCM        = null;
let _adminCntConcursos             = 0;
let _adminCntCM                    = 0;
let _ultimasNotifs                 = [];

const TIPO_LABEL = {
    resultado_disponible:         '📊 Resultados disponibles',
    certificado_disponible:       '🏅 Certificados disponibles',
    acceso_managed_aprobado:      '☁️ Acceso Contest Manager aprobado',
    solicitud_concurso_aprobada:  '✅ Solicitud de concurso aprobada',
    solicitud_concurso_rechazada: '❌ Solicitud de concurso rechazada',
};

// ── Iniciar escucha al hacer login ────────────────────────────

export function iniciarEscuchaNotificaciones(uid) {
    if (_cancelarEscucha) _cancelarEscucha();

    const q = query(
        collection(db, 'notificaciones'),
        where('userId', '==', uid),
        orderBy('creadaEn', 'desc'),
        limit(30)
    );

    _cancelarEscucha = onSnapshot(q, (snapshot) => {
        const notifs = snapshot.docs.map(d => ({ id: d.id, ...d.data(), creadaEn: d.data().creadaEn?.toDate?.() ?? null }));
        _ultimasNotifs = notifs;
        _actualizarBell(notifs);
        _renderizarDropdown(notifs);
    });
}

export function detenerEscuchaNotificaciones() {
    if (_cancelarEscucha) { _cancelarEscucha(); _cancelarEscucha = null; }
    _ultimasNotifs = [];
    _actualizarBell([]);
}

export function iniciarEscuchaAdminPendientes() {
    _cancelarEscuchaAdminConcursos?.();
    _cancelarEscuchaAdminCM?.();

    _cancelarEscuchaAdminConcursos = onSnapshot(
        query(collection(db, 'solicitudesConcurso'), where('estado', '==', 'pendiente')),
        (snap) => { _adminCntConcursos = snap.size; _actualizarBell(_ultimasNotifs); _renderizarDropdown(_ultimasNotifs); },
        () => {}
    );
    _cancelarEscuchaAdminCM = onSnapshot(
        query(collection(db, 'solicitudesManagedDrive'), where('estado', '==', 'pendiente')),
        (snap) => { _adminCntCM = snap.size; _actualizarBell(_ultimasNotifs); _renderizarDropdown(_ultimasNotifs); },
        () => {}
    );
}

export function detenerEscuchaAdminPendientes() {
    _cancelarEscuchaAdminConcursos?.();
    _cancelarEscuchaAdminCM?.();
    _cancelarEscuchaAdminConcursos = null;
    _cancelarEscuchaAdminCM        = null;
    _adminCntConcursos             = 0;
    _adminCntCM                    = 0;
}

// ── Bell badge ────────────────────────────────────────────────

function _actualizarBell(notifs) {
    const sinLeer    = notifs.filter(n => !n.leida).length;
    const adminTotal = _adminCntConcursos + _adminCntCM;
    const total      = sinLeer + adminTotal;
    const badge      = document.getElementById('notif-badge');
    const bell       = document.getElementById('btn-notificaciones');

    if (badge) {
        badge.textContent   = total > 9 ? '9+' : String(total);
        badge.style.display = total > 0 ? 'flex' : 'none';
    }
    if (bell) bell.dataset.sinLeer = String(total);
}

// ── Dropdown ──────────────────────────────────────────────────

function _renderizarDropdown(notifs) {
    const lista = document.getElementById('notif-lista');
    if (!lista) return;

    lista.replaceChildren();

    const adminTotal = _adminCntConcursos + _adminCntCM;

    if (adminTotal > 0) {
        const adminItem = document.createElement('div');
        adminItem.className = 'notif-item notif-item--admin notif-item--sinleer';

        const titulo = document.createElement('span');
        titulo.className = 'notif-item__titulo';
        titulo.textContent = '⚙️ Solicitudes administrativas';

        const sub = document.createElement('span');
        sub.className = 'notif-item__sub';
        sub.textContent = `${adminTotal} solicitud${adminTotal !== 1 ? 'es' : ''} pendiente${adminTotal !== 1 ? 's' : ''} requieren revisión.`;

        const enlace = document.createElement('a');
        enlace.href = '/admin';
        enlace.className = 'notif-admin-link';
        enlace.textContent = 'Ir al panel de administración →';

        adminItem.append(titulo, sub, enlace);
        lista.appendChild(adminItem);
    }

    if (notifs.length === 0 && adminTotal === 0) {
        const vacio = document.createElement('p');
        vacio.className = 'notif-vacio';
        vacio.textContent = 'Sin notificaciones';
        lista.appendChild(vacio);
        return;
    }

    notifs.forEach(n => {
        const item = document.createElement('div');
        item.className = `notif-item${n.leida ? '' : ' notif-item--sinleer'}`;

        const titulo = document.createElement('span');
        titulo.className = 'notif-item__titulo';
        titulo.textContent = TIPO_LABEL[n.tipo] ?? n.tipo;

        const sub = document.createElement('span');
        sub.className = 'notif-item__sub';
        sub.textContent = n.mensaje || `${n.concurso} ${n.año} — ${n.modo}`;

        item.append(titulo, sub);

        if (!n.leida) {
            item.addEventListener('click', () => {
                marcarNotificacionLeida(n.id).catch(console.error);
            });
        }

        lista.appendChild(item);
    });
}

// ── Toggle dropdown ───────────────────────────────────────────

let _dropdownAbierto = false;

export function toggleNotificaciones() {
    const dropdown = document.getElementById('notif-dropdown');
    if (!dropdown) return;

    _dropdownAbierto = !_dropdownAbierto;
    dropdown.classList.toggle('notif-dropdown--visible', _dropdownAbierto);

    if (_dropdownAbierto) {
        setTimeout(() => {
            document.addEventListener('click', _cerrarDropdownExterno);
        }, 0);
    } else {
        document.removeEventListener('click', _cerrarDropdownExterno);
    }
}

export function cerrarDropdownNotificaciones() {
    const dropdown = document.getElementById('notif-dropdown');
    dropdown?.classList.remove('notif-dropdown--visible');
    document.removeEventListener('click', _cerrarDropdownExterno);
    _dropdownAbierto = false;
}

function _cerrarDropdownExterno(e) {
    const dropdown = document.getElementById('notif-dropdown');
    const bell     = document.getElementById('btn-notificaciones');
    if (dropdown?.contains(e.target) || bell?.contains(e.target)) return;
    cerrarDropdownNotificaciones();
}

export async function manejarMarcarTodasLeidas() {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    await marcarTodasLeidas(uid).catch(console.error);
}

// ── Operaciones Firestore de notificaciones ───────────────────

export async function marcarNotificacionLeida(id) {
    await updateDoc(doc(db, 'notificaciones', id), { leida: true });
}

export async function marcarTodasLeidas(uid) {
    const q = query(
        collection(db, 'notificaciones'),
        where('userId', '==', uid),
        where('leida',  '==', false)
    );
    const snap = await getDocs(q);
    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.update(d.ref, { leida: true }));
    await batch.commit();
}

export async function obtenerNotificacionesUsuario(uid, soloSinLeer = false) {
    const condiciones = [where('userId', '==', uid)];
    if (soloSinLeer) condiciones.push(where('leida', '==', false));

    const q = query(
        collection(db, 'notificaciones'),
        ...condiciones,
        orderBy('creadaEn', 'desc'),
        limit(30)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data(), creadaEn: d.data().creadaEn?.toDate?.() ?? null }));
}

/**
 * Crea notificaciones in-app para todos los usuarios que participaron
 * en el mismo concurso/año/modo, excepto el que subió el archivo.
 * Retorna lista de { userId, email, prefs } para el caller (emails).
 * REQUIERE índice compuesto Firestore: editions (concurso ASC, año ASC, modo ASC)
 */
export async function crearNotificacionesArchivo({ tipo, concurso, año, modo, archivoUrl, subidoPor }) {
    const tipoNotif = tipo === 'res' ? 'resultado_disponible' : 'certificado_disponible';

    const q = query(
        collection(db, 'editions'),
        where('concurso', '==', concurso),
        where('año',      '==', año),
        where('modo',     '==', modo)
    );
    const snap = await getDocs(q);

    const destinatarios = [];

    for (const edicionDoc of snap.docs) {
        const uid = edicionDoc.data().userId;
        if (!uid || uid === subidoPor) continue;

        await addDoc(collection(db, 'notificaciones'), {
            userId:    uid,
            tipo:      tipoNotif,
            concurso,
            año,
            modo,
            archivoUrl: archivoUrl ?? null,
            leida:     false,
            creadaEn:  serverTimestamp(),
        });

        try {
            const userSnap = await getDoc(doc(db, 'users', uid));
            if (userSnap.exists()) {
                const d = userSnap.data();
                destinatarios.push({
                    userId: uid,
                    email:  d.email || '',
                    nombre: d.nombre || d.displayName || '',
                    prefs:  d.preferencias?.notificaciones ?? {},
                });
            }
        } catch { /* sin datos de usuario — no bloquear */ }
    }

    return destinatarios;
}
